import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { MoreOutlined } from "us.icons";
import {
  $Button,
  $Skeleton,
  $TableTree,
  ITableTreeColumns,
  $Popover,
  $Tooltip,
} from "us.common/components";
import { INITIAL_ITEM_MENU, COLUMNS } from "./Constants";
import { ItemMenu } from "./Components";
import { ClosureCodesColumns } from "us.collection.admin/components/ClosureCodes/Constants";
import { ClosureCodesActions } from "us.collection.admin/constants";
import * as Actions from "us.collection.admin/actions";

/**
 * @description - Manage Closure Code home view component
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 11/07/2024
 */

const ClosureCodesTable: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const { closureCodeList, openDrawer, deleteClosureCode } = props;
  const { data, isLoading } = closureCodeList ?? {};

  const [itemMenu, setItemMenu] =
    useState<typeof INITIAL_ITEM_MENU>(INITIAL_ITEM_MENU);

  /**
   * Delete closure code
   * @param codeId - Closure code id
   */
  const deleteCode = (codeId: number) => {
    deleteClosureCode && deleteClosureCode({ codeId });
  };

  const handleItemMenus = (actionType: ClosureCodesActions, codeId: number) => {
    setItemMenu({
      visible: false,
      codeId: codeId,
    });
    if (actionType == ClosureCodesActions.DELETE) {
      deleteCode(codeId);
    } else {
      openDrawer &&
        openDrawer({
          isVisible: true,
          codeId: codeId,
          action: actionType,
        });
    }
  }

  /**
   * @function
   * @description Get table columns list
   * @returns {ITableTreeColumns} - TableTree columns
   */
  const getColumns = (): ITableTreeColumns => {
    const columns: ITableTreeColumns = [];
    COLUMNS.map(({ key, title, width }: any) => {
      let column: any = {
        key: key,
        dataIndex: key,
        title: title && t(title),
        width,
      };
      if (key === ClosureCodesColumns.MENU) {
        column = {
          ...column,
          align: "center",
          customRenderChild: (_text: any, record: any) => (
            <div className="align-items-center" key={0}>
              <$Popover
                placement="rightTop"
                trigger="click"
                open={itemMenu.visible && itemMenu?.codeId === record.id}
                onVisibleChange={(visible: boolean) => {
                  setItemMenu({
                    visible,
                    codeId: record.id,
                  });
                }}
                content={
                  <ItemMenu
                    onCallAction={(actionType: ClosureCodesActions) => handleItemMenus(actionType, record?.id)}
                  />
                }
                destroyTooltipOnHide
              >
                <$Button icon={<MoreOutlined  />} size="small" />
              </$Popover>
            </div>
          ),
        };
      } else {
        column = {
          ...column,
          customFilter: true,
          customSorter: (a: any, b: any) => a?.localeCompare(b),
          customRenderChild: (text: any, record: any) => {
            if (key == ClosureCodesColumns.ARCHIVE_IN) {
              return (
                <>
                  {record?.archiveDuration > 0 && (
                    <span>
                      {record?.archiveDuration} {record?.archiveDurationPeriod}
                    </span>
                  )}
                </>
              );
            } else if (key == ClosureCodesColumns.DELETE_IN) {
              return (
                <>
                  {record?.deletionDuration > 0 && (
                    <span>
                      {record?.deletionDuration}{" "}
                      {record?.deletionDurationPeriod}
                    </span>
                  )}
                </>
              );
            } else if (key == ClosureCodesColumns.ARCHIVE_FROM) {
              return (
                <span>
                  {record?.isStartFromCaseClosedDate
                    ? t(
                        "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.CASE_CLOSE_DATE"
                      )
                    : t(
                        "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.NEXT_CALENDAR_PERIOD"
                      )}
                </span>
              );
            } else if (key == ClosureCodesColumns.BAD_DEBT_RECORD) {
              return <span>{record[key]}</span>;
            } else {
              return (
                <$Tooltip
                  placement="topLeft"
                  title={record[key]}
                  key={record[key]}
                >
                  {record[key]}
                </$Tooltip>
              );
            }
          },
          ellipsis: {
            showTitle: true,
          },
        };
      }
      columns.push(column);
    });

    return columns;
  };

  return (
    <$Skeleton
      loading={isLoading}
      active
      paragraph={{
        rows: 2,
      }}
    >
      <$TableTree
        rowKey={(record: any) => record.id}
        data={data}
        size="small"
        filterOnType
        bordered
        className="mt-3"
        onSort={(sortData, dataSource) => {
          return sortData(dataSource);
        }}
        onFilter={(searchData, dataSource) => {
          return searchData(dataSource);
        }}
        pagination={false}
        scroll={{ x: "100%", y: "calc(100vh - 160px)" }}
        columns={getColumns()}
        locale={{
          filterConfirm: t("COMMON.OK"),
          filterReset: t("COMMON.RESET"),
        }}
        resetOnSourceChange={true}
        virtual={true}
        onRow={(record, _rowIndex) => {
          return {
            onDoubleClick: () =>
              openDrawer &&
              openDrawer({
                isVisible: true,
                codeId: record.id,
                action: ClosureCodesActions.EDIT,
              }),
          };
        }}
      />
    </$Skeleton>
  );
};

const mapStateToProps = (state: RootState) => {
  const { closureCodes } = state;
  const { closureCodeList } = closureCodes;
  return {
    closureCodeList,
  };
};

const { newClosureCode, closureCodeDeletion } = Actions.closureCodes;

const mapDispatchToProps = {
  openDrawer: newClosureCode.openDrawer,
  deleteClosureCode: closureCodeDeletion.delete,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClosureCodesTable);
